import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, Grid, Theme, Typography } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import { createStyles, makeStyles } from '@material-ui/styles';
import { getIntelliCheckReport } from 'api/file/file-api';
import { DocType, IntelliCheckReportResponse } from 'api/file/interfaces/get-file-order-insights';
import { useSearchParams } from 'features/help/hooks/useSearchParams';
import useAsync from 'hooks/useAsync';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import IdentityInfoTable from './components/IdentityInfoTable';
import ScanImages from './components/ScanImages';
import SummaryInfo from './components/SummaryInfo';
import { getIdentityFields } from './utils/getIdentityFields';

const IntellicheckReport = () => {
  const { fileId, transactionId } = useParams<{ fileId: string; transactionId: string }>();
  const { pdfToken, tz } = useSearchParams<{ pdfToken?: string; tz?: string }>();
  const [reportData, setReportData] = useState<IntelliCheckReportResponse | null>(null);

  const getIntelliCheckReportData = async (): Promise<IntelliCheckReportResponse> => {
    return getIntelliCheckReport(
      fileId ?? '',
      pdfToken ?? '',
      transactionId ?? '',
    ) as Promise<IntelliCheckReportResponse>;
  };
  const { execute: executeIntelliCheckReport, value: intelliCheckReportData } = useAsync<IntelliCheckReportResponse>(
    getIntelliCheckReportData,
    false,
  );

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      fullWidth: {
        width: '100%',
        background: theme.palette.background.paper,
      },
      pdfBody: {
        width: '8.5in',
        padding: '0',
        margin: theme.spacing(0, 'auto'),
        background: `${theme.palette.background.paper} !important`,
        border: `6px solid ${theme.palette.background.paper} !important`,
        boxShadow: 'none !important',
      },
      pageBreak: {
        pageBreakAfter: 'always',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        height: '8.5in',
        paddingTop: '1in',
      },
      blueBar: {
        background: 'rgba(1, 58, 111, 0.15)',
        borderBottom: '1px solid rgba(1, 58, 111, 0.50)',
        display: 'flex',
        pageBreakInside: 'avoid',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(0, 1.25),
        margin: theme.spacing(2, 0),
      },
      pdfPage: {
        pageBreakAfter: 'always',
        padding: theme.spacing(2.5),
      },

      lastPdfPage: {
        pageBreakAfter: 'auto',
      },

      pdfPageBreak: {
        display: 'block',
        pageBreakBefore: 'always',
      },
      phoneNumber: {
        fontSize: pxToRem(12),
        fontWeight: 500,
        marginBottom: pxToRem(16),
      },
      phoneNumberLabel: {
        fontFamily: 'Avenir',
      },
      phoneNumberValue: {
        fontWeight: 350,
      },
      caps: {
        fontSize: '11pt',
        fontWeight: 600,
        letterSpacing: '1px',
        color: theme.palette.highlightIcon.main,
        textTransform: 'uppercase',
      },
      pageTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(0.5),
      },
      titleText: {
        fontWeight: 800,
        fontSize: pxToRem(22),
        color: theme.palette.highlightIcon.main,
      },
      titleIcon: {
        width: 16,
        height: 16,
      },
      titleSuccessIcon: {
        color: theme.palette.success.main,
      },
      titleFailIcon: {
        color: theme.palette.error.main,
      },
      titleVerificationText: {
        textTransform: 'uppercase',
        fontWeight: 800,
        fontSize: pxToRem(11),
      },
      verifiedText: {
        color: theme.palette.success.dark,
      },
      unVerifiedText: {
        color: theme.palette.error.dark,
      },
      verificationWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
      },
      documentMismatch: {
        color: '#5F2120',
        fontWeight: 600,
        marginRight: theme.spacing(1),
      },
      mismatchIcon: {
        color: theme.palette.error.main,
      },
    }),
  );

  const classes = useStyles();

  useEffect(() => {
    executeIntelliCheckReport().then();
    if (fileId && transactionId) {
      document.title = 'Identity Verification Report';
    }
  }, [fileId, transactionId]);

  useEffect(() => {
    setReportData(intelliCheckReportData);
  }, [intelliCheckReportData]);

  const isDocumentMismatch =
    (reportData?.submittedData?.docType === DocType.NORTH_AMERICAN_DRIVER_LICENSE &&
      reportData?.reportData?.idcheck?.data?.docCategory !== DocType.DL) ||
    (reportData?.submittedData?.docType === DocType.PASSPORT &&
      reportData?.reportData?.extendedId?.data?.documentType?.toLowerCase() !== DocType.PASSPORT);

  const {
    fields: identityFields,
    isPassportOrOther,
    isDL,
  } = reportData?.reportData
    ? getIdentityFields(reportData?.reportData, reportData?.submittedData)
    : { fields: [], isPassportOrOther: false, isDL: false };

  const isPhoneSubmitted = !!reportData?.submittedData?.phoneNumber;

  return (
    <div className={classes.fullWidth}>
      {reportData && (
        <div id="pdf-content" className={classes.pdfBody}>
          <div className={classes.pdfPage}>
            <Grid item sm={12} className={classes.pageTitle}>
              <Typography className={classes.titleText}>Identity Verification Report</Typography>
              {isDocumentMismatch && (
                <Box display="flex" alignItems="center">
                  <Typography className={classes.documentMismatch}>DOCUMENT MISMATCH</Typography>
                  <CancelIcon className={classes.mismatchIcon} />
                </Box>
              )}
            </Grid>

            <div className={classes.blueBar}>
              <p className={classes.caps}>Summary</p>
            </div>
            <SummaryInfo summary={reportData.summary} isPassport={isPassportOrOther} timezone={tz} />

            <div className={classes.blueBar}>
              <p className={classes.caps}>Identity Images</p>
            </div>
            <ScanImages {...reportData.reportData?.images} />
          </div>

          <div className={classes.pdfPageBreak} />

          <div className={`${classes.pdfPage} ${classes.lastPdfPage}`}>
            <div className={classes.blueBar}>
              <p className={classes.caps}>Identity Information</p>
            </div>
            <Typography className={classes.phoneNumber}>
              <span className={classes.phoneNumberLabel}>{isPhoneSubmitted ? 'Phone Number: ' : 'Email: '}</span>
              <span className={classes.phoneNumberValue}>
                {isPhoneSubmitted
                  ? reportData.reportData?.ipqs?.phoneDetails?.data?.formatted
                  : reportData.submittedData?.emailAddress}
              </span>
            </Typography>
            <IdentityInfoTable fields={identityFields} isPassport={isPassportOrOther} isDL={isDL} />
          </div>
        </div>
      )}
    </div>
  );
};

export default IntellicheckReport;
